// src/components/Favorites.js

import React, { useRef, useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FavoritesContext } from "../context/FavoritesContext";
import { ReactComponent as CloseIcon } from "../icons/cancel.svg";
import { useTranslation } from "react-i18next";
import {
  CurrencyContext,
  pricePlusSurcharge,
} from "../context/CurrencyContext";
import FittingCalendar from "./FittingCalendar";
import FittingPayment from "./FittingPayment";
import VoiceNotePlayer from "./VoiceNotePlayer";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const Favorites = ({ drawerOpen, setDrawerOpen }) => {
  const { t, i18n } = useTranslation();
  const { favorites, toggleFavorite } = useContext(FavoritesContext);
  const { currency, exchangeRates } = useContext(CurrencyContext);

  const navigate = useNavigate();
  const drawerRef = useRef();

  // Fitting Appointment State
  const [showFitting, setShowFitting] = useState(false);
  const [fittingStep, setFittingStep] = useState("calendar");
  const [appointmentData, setAppointmentData] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "+33",
  });
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setDrawerOpen(false);
      }
    };

    if (drawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [drawerOpen, setDrawerOpen]);

  // Reset fitting state when drawer closes
  useEffect(() => {
    if (!drawerOpen) {
      setShowFitting(false);
      setFittingStep("calendar");
      setAppointmentData(null);
      setCustomerInfo({ name: "", email: "", phone: "+33" });
      setConfirmedAppointment(null);
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (drawerOpen && drawerRef.current) {
      drawerRef.current.scrollTop = 0;
    }
  }, [drawerOpen]);

  const handleProductClick = (product) => {
    if (!product.category || !product.id) {
      console.error(
        "Missing category or productId for favorite item:",
        product,
      );
      return;
    }
    navigate(
      `/full-product?category=${encodeURIComponent(product.category)}&productId=${encodeURIComponent(product.id)}`,
    );
    setDrawerOpen(false);
  };

  const isMobile = window.innerWidth < 768;

  const calculateTotal = () =>
    favorites.reduce(
      (sum, it) =>
        sum + pricePlusSurcharge(+it.price, currency, exchangeRates).numeric,
      0,
    );

  const frequentlyBoughtItems = favorites
    .flatMap((item) => item.selectedForYou || [])
    .reduce((uniqueItems, currentItem) => {
      if (!uniqueItems.some((itm) => itm.name === currentItem.name)) {
        uniqueItems.push(currentItem);
      }
      return uniqueItems;
    }, []);

  // Date formatting helper - supports French
  const formatDateForDisplay = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "";

    const options = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    let dateString = date.toLocaleDateString(
      i18n.language === "fr" ? "fr-FR" : "en-GB",
      options,
    );
    // Capitalize first letter
    return dateString.charAt(0).toUpperCase() + dateString.slice(1);
  };

  // Fitting Appointment Handlers
  const handleStartFitting = () => {
    setShowFitting(true);
    setFittingStep("calendar");
  };

  const handleCloseFitting = () => {
    setShowFitting(false);
    setFittingStep("calendar");
    setAppointmentData(null);
    setCustomerInfo({ name: "", email: "", phone: "+33" });
  };

  const handleConfirmSlot = (data) => {
    const dateFormatted = formatDateForDisplay(data.date);
    setAppointmentData({ ...data, dateFormatted });
    setFittingStep("details");
  };

  const handleSubmitDetails = (e) => {
    e.preventDefault();
    if (customerInfo.name && customerInfo.email && customerInfo.phone) {
      setFittingStep("payment");
    }
  };

  const handlePaymentSuccess = (appointment) => {
    setConfirmedAppointment(appointment);
    setFittingStep("success");
  };

  const handleBackToCalendar = () => {
    setFittingStep("calendar");
    setAppointmentData(null);
  };

  const handleBackToDetails = () => {
    setFittingStep("details");
  };

  const handleFittingDone = () => {
    setShowFitting(false);
    setFittingStep("calendar");
    setAppointmentData(null);
    setCustomerInfo({ name: "", email: "", phone: "+33" });
    setConfirmedAppointment(null);
    setDrawerOpen(false);
  };

  // Create combined product info for fitting
  const getCombinedProductInfo = () => {
    if (favorites.length === 0) return null;
    const productNames = favorites.map((item) => item.name).join(", ");
    return {
      productId: favorites.map((item) => item.productId || item.id).join(","),
      name:
        favorites.length === 1
          ? favorites[0].name
          : `${favorites.length} items: ${productNames}`,
      price: calculateTotal().toString(),
      image: favorites[0].image,
    };
  };

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: drawerOpen ? "rgba(0, 0, 0, 0.5)" : "transparent",
      transition: "background-color 0.3s ease",
      zIndex: 1000,
      display: drawerOpen ? "block" : "none",
    },
    drawer: {
      position: "fixed",
      top: 0,
      right: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      borderLeft: "1px solid rgba(0, 0, 0, 0.1)",
      transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.3s ease",
      zIndex: 1001,
      padding: "20px",
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box",
      overflow: "hidden",
    },
    drawerWide: {
      width: "450px",
    },
    drawerHeader: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "10px",
      position: "relative",
      paddingTop: isMobile ? "20px" : "0px",
    },
    icon: {
      cursor: "pointer",
      width: isMobile ? "24px" : "16px",
      height: isMobile ? "24px" : "16px",
      position: "absolute",
      left: isMobile ? "5px" : "0px",
    },
    title: {
      fontFamily: "Georgia",
      color: "#707070",
      opacity: 0.9,
      fontSize: isMobile ? "20px" : "14px",
      textAlign: "center",
    },
    hr: {
      width: "100%",
      border: "none",
      borderTop: "2px solid rgba(0, 0, 0, 0.2)",
      margin: "10px 0",
    },
    content: {
      display: "flex",
      flexDirection: "column",
      fontFamily: "Georgia",
      color: "#707070",
      fontSize: "16px",
      textAlign: "center",
      height: "calc(100% - 60px)",
      justifyContent: favorites.length === 0 ? "center" : "flex-start",
      alignItems: favorites.length === 0 ? "center" : "flex-start",
      paddingLeft: isMobile ? "10px" : "0px",
      paddingRight: isMobile ? "10px" : "0px",
      boxSizing: "border-box",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      overflowX: "hidden",
    },
    favoriteItem: {
      display: "flex",
      alignItems: "flex-start",
      marginBottom: "10px",
      textAlign: "left",
      width: "100%",
      cursor: "pointer",
    },
    favoriteItemImageContainer: {
      flex: "0 0 auto",
      marginRight: "10px",
    },
    favoriteItemImage: {
      width: "80px",
      height: "100px",
      objectFit: "cover",
    },
    favoriteItemDetailsContainer: {
      flex: "1 1 auto",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
    },
    favoriteItemName: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#000",
      marginBottom: "5px",
    },
    favoriteItemPrice: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#000",
    },
    removeButtonContainer: {
      flex: "0 0 auto",
      textAlign: "right",
      alignSelf: "flex-start",
    },
    removeButton: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#000",
      textDecoration: "underline",
      cursor: "pointer",
    },
    fittingButton: {
      marginTop: "20px",
      padding: "15px 20px",
      border: "none",
      backgroundColor: "#000",
      color: "#FFF",
      fontSize: "14px",
      fontFamily: "Georgia",
      cursor: "pointer",
      textAlign: "center",
      width: "100%",
      boxSizing: "border-box",
    },
    totalContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      paddingLeft: isMobile ? "10px" : "0px",
      paddingRight: isMobile ? "10px" : "0px",
      boxSizing: "border-box",
      fontFamily: "Georgia",
      fontSize: "16px",
      color: "#000",
      marginTop: "10px",
      marginBottom: "10px",
    },
    frequentlyBoughtContainer: {
      width: "100%",
      boxSizing: "border-box",
      marginTop: "20px",
      marginBottom: "20px",
    },
    frequentlyBoughtTitle: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#000",
      textAlign: "center",
      marginTop: "30px",
      marginBottom: "20px",
    },
    frequentlyBoughtGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "10px",
    },
    frequentlyBoughtItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      boxSizing: "border-box",
      cursor: "pointer",
    },
    frequentlyBoughtImage: {
      width: "100%",
      height: "170px",
      objectFit: "cover",
      marginBottom: "5px",
    },
    frequentlyBoughtName: {
      fontFamily: "Georgia",
      fontSize: "12px",
      color: "#000",
      textAlign: "left",
      marginBottom: "10px",
    },
    emptyCartText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: "20px",
    },
    // Fitting Flow Styles
    fittingContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "15px",
    },
    fittingHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px",
    },
    fittingTitle: {
      fontFamily: "Georgia",
      fontSize: "16px",
      color: "#000",
      fontWeight: "500",
    },
    fittingBackButton: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#707070",
      cursor: "pointer",
      textDecoration: "underline",
    },
    detailsForm: {
      display: "flex",
      flexDirection: "column",
      gap: "15px",
      width: "100%",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      textAlign: "left",
    },
    formLabel: {
      fontFamily: "Georgia",
      fontSize: "13px",
      color: "#555",
    },
    formInput: {
      padding: "12px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      fontFamily: "Georgia",
      fontSize: "14px",
    },
    buttonRow: {
      display: "flex",
      gap: "10px",
      marginTop: "10px",
    },
    backButton: {
      flex: "1",
      padding: "12px",
      border: "1px solid #000",
      backgroundColor: "#fff",
      color: "#000",
      fontFamily: "Georgia",
      fontSize: "14px",
      cursor: "pointer",
    },
    continueButton: {
      flex: "2",
      padding: "12px",
      border: "none",
      backgroundColor: "#000",
      color: "#fff",
      fontFamily: "Georgia",
      fontSize: "14px",
      cursor: "pointer",
    },
    successContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px",
      textAlign: "center",
      gap: "15px",
    },
    successIcon: {
      marginBottom: "10px",
    },
    successTitle: {
      fontFamily: "Georgia",
      fontSize: "18px",
      color: "#000",
      fontWeight: "500",
    },
    successText: {
      fontFamily: "Georgia",
      fontSize: "14px",
      color: "#555",
    },
    successDetails: {
      backgroundColor: "#f9f9f9",
      padding: "15px",
      borderRadius: "4px",
      width: "100%",
      textAlign: "left",
    },
    doneButton: {
      padding: "15px 40px",
      border: "none",
      backgroundColor: "#000",
      color: "#fff",
      fontFamily: "Georgia",
      fontSize: "14px",
      cursor: "pointer",
      marginTop: "10px",
    },
  };

  const globalStyles = `
    ::-webkit-scrollbar {
      display: none;
    }
  `;

  if (typeof document !== "undefined") {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = globalStyles;
    document.head.appendChild(styleSheet);
  }

  // Render Fitting Flow
  const renderFittingFlow = () => {
    if (fittingStep === "calendar") {
      return (
        <div style={styles.fittingContainer}>
          <div style={styles.fittingHeader}>
            <span style={styles.fittingTitle}>{t("Fitting Appointment")}</span>
            <span style={styles.fittingBackButton} onClick={handleCloseFitting}>
              {t("Cancel")}
            </span>
          </div>
          <VoiceNotePlayer
            key={i18n.language}
            autoPlay
            audioSrcEn="/voicenote_engl.mp3"
            audioSrcFr="/voicenote_fren.mp3"
          />
          <FittingCalendar onConfirm={handleConfirmSlot} />
        </div>
      );
    }

    if (fittingStep === "details" && appointmentData) {
      return (
        <div style={styles.fittingContainer}>
          <div style={styles.fittingHeader}>
            <span style={styles.fittingTitle}>{t("Your Details")}</span>
          </div>
          <p
            style={{
              fontFamily: "Georgia",
              fontSize: "14px",
              color: "#555",
              margin: 0,
              textAlign: "center",
            }}
          >
            {appointmentData.dateFormatted}{" "}
            {i18n.language === "fr" ? "à" : "at"} {appointmentData.slot}
          </p>
          <form onSubmit={handleSubmitDetails} style={styles.detailsForm}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{t("Full Name")}</label>
              <input
                type="text"
                style={styles.formInput}
                value={customerInfo.name}
                onChange={(e) =>
                  setCustomerInfo({ ...customerInfo, name: e.target.value })
                }
                placeholder={t("Enter your full name")}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{t("Email")}</label>
              <input
                type="email"
                style={styles.formInput}
                value={customerInfo.email}
                onChange={(e) =>
                  setCustomerInfo({ ...customerInfo, email: e.target.value })
                }
                placeholder={t("Enter your email")}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{t("Phone number")}</label>
              <PhoneInput
                country={"fr"}
                value={customerInfo.phone}
                onChange={(phone) =>
                  setCustomerInfo({ ...customerInfo, phone })
                }
                inputStyle={{ width: "100%" }}
                placeholder={t("Phone number")}
                enableSearch={true}
              />
            </div>
            <div style={styles.buttonRow}>
              <button
                type="button"
                style={styles.backButton}
                onClick={handleBackToCalendar}
              >
                {t("Back")}
              </button>
              <button type="submit" style={styles.continueButton}>
                {t("Continue to Payment")}
              </button>
            </div>
          </form>
        </div>
      );
    }

    if (fittingStep === "payment" && appointmentData) {
      return (
        <div style={styles.fittingContainer}>
          <FittingPayment
            appointmentData={appointmentData}
            customerInfo={customerInfo}
            productInfo={getCombinedProductInfo()}
            onSuccess={handlePaymentSuccess}
            onCancel={handleBackToDetails}
          />
        </div>
      );
    }

    if (fittingStep === "success" && confirmedAppointment) {
      return (
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
              <circle cx="30" cy="30" r="30" fill="#10B981" />
              <path
                d="M20 30L27 37L40 24"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 style={styles.successTitle}>{t("Appointment Confirmed!")}</h3>
          <p style={styles.successText}>
            {t("Your fitting appointment has been booked.")}
          </p>
          <div style={styles.successDetails}>
            <p>
              <strong>{t("Date")}:</strong> {appointmentData.dateFormatted}
            </p>
            <p>
              <strong>{t("Time")}:</strong> {appointmentData.slot} (Paris Time)
            </p>
            <p>
              <strong>{t("Email")}:</strong> {customerInfo.email}
            </p>
          </div>
          <p style={styles.successText}>
            {t(
              "A confirmation email has been sent to your email address. The €50 deposit will be deducted from your final order.",
            )}
          </p>
          <button style={styles.doneButton} onClick={handleFittingDone}>
            {t("Done")}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {drawerOpen && (
        <div style={styles.overlay} onClick={() => setDrawerOpen(false)}></div>
      )}
      <div
        style={{
          ...styles.drawer,
          ...(window.innerWidth >= 768 ? styles.drawerWide : {}),
        }}
      >
        <div style={styles.drawerHeader}>
          <CloseIcon style={styles.icon} onClick={() => setDrawerOpen(false)} />
          <div style={styles.title}>{t("Favorites")}</div>
        </div>
        <div
          style={{
            paddingLeft: isMobile ? "10px" : "0px",
            paddingRight: isMobile ? "10px" : "0px",
          }}
        >
          <hr style={styles.hr} />
        </div>

        <div style={styles.content} ref={drawerRef}>
          {favorites.length === 0 ? (
            <div style={styles.emptyCartText}>
              <div>{t("You have no favorite items.")}</div>
            </div>
          ) : showFitting ? (
            renderFittingFlow()
          ) : (
            <>
              {favorites.map((item, index) => (
                <React.Fragment key={index}>
                  <div
                    style={styles.favoriteItem}
                    onClick={() => handleProductClick(item)}
                  >
                    <div style={styles.favoriteItemImageContainer}>
                      <img
                        src={item.image}
                        alt={item.name}
                        style={styles.favoriteItemImage}
                      />
                    </div>
                    <div style={styles.favoriteItemDetailsContainer}>
                      <div style={styles.favoriteItemName}>{item.name}</div>
                      <div style={styles.favoriteItemPrice}>
                        {
                          pricePlusSurcharge(
                            +item.price,
                            currency,
                            exchangeRates,
                          ).formatted
                        }
                      </div>
                    </div>
                    <div style={styles.removeButtonContainer}>
                      <div
                        style={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(item);
                        }}
                      >
                        {t("Remove")}
                      </div>
                    </div>
                  </div>
                  <hr style={styles.hr} />
                </React.Fragment>
              ))}

              <div style={styles.totalContainer}>
                <div>{t("Total")}</div>
                <div>
                  {
                    pricePlusSurcharge(
                      calculateTotal(),
                      currency,
                      exchangeRates,
                    ).formatted
                  }
                </div>
              </div>

              <div style={styles.fittingButton} onClick={handleStartFitting}>
                {t("Fitting Appointment")}
              </div>

              <div style={styles.frequentlyBoughtContainer}>
                <div style={styles.frequentlyBoughtTitle}>
                  {t("Frequently bought together")}
                </div>
                {frequentlyBoughtItems.length === 0 ? (
                  <div>
                    {t("No items found for Frequently Bought Together.")}
                  </div>
                ) : (
                  <div style={styles.frequentlyBoughtGrid}>
                    {frequentlyBoughtItems.map((item, index) => (
                      <div
                        key={index}
                        style={styles.frequentlyBoughtItem}
                        onClick={() => handleProductClick(item)}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          style={styles.frequentlyBoughtImage}
                        />
                        <div style={styles.frequentlyBoughtName}>
                          {item.name} -{" "}
                          {
                            pricePlusSurcharge(
                              +item.price,
                              currency,
                              exchangeRates,
                            ).formatted
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Favorites;
